import React from "react";
import { Button, Flex, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { useAppSelector } from "../../../../hooks";
import { getUserUpnFromEmail } from "../../../utils";
import type { IChecklist } from "@mctypes/checklist.types";
import { ChecklistCoreActionModal } from "./ChecklistActionModal";
import { RelatedChecklistsByReviewIdMenu } from "./RelatedChecklistsByReviewIdMenu";
import DkApprovalPanel from "./DkApprovalPanel";

const hideLegacyEkSkActions = true;

const DoksamChecklistBanner: React.FC<{ checklist: IChecklist }> = ({ checklist }) => {
  const [showSendToSkModal, setShowSendToSkModal] = React.useState(false);
  const [showSendToEkModal, setShowSendToEkModal] = React.useState(false);
  const [showEkSendToGodkjenningModal, setShowEkSendToGodkjenningModal] = React.useState(false);

  const user = useAppSelector((state) => state.globalReducer.user);

  // Normaliser UPN til lowercase for trygg sammenligning
  const userUpn = (getUserUpnFromEmail(user?.email || "") || "").toLowerCase();
  const ekUpn = checklist?.doksam?.check1UserUpn?.toLowerCase();
  const skUpn = checklist?.doksam?.check2UserUpn?.toLowerCase();

  const loggedInUserIsEK = userUpn !== "" && ekUpn === userUpn;
  const loggedInUserIsSK = userUpn !== "" && skUpn === userUpn;
  const showLegacyEkSkActions = !hideLegacyEkSkActions;

  const docId = checklist?.doksam?.docId ?? "";

  return (
    <>
      {showLegacyEkSkActions && showSendToEkModal && (
        <ChecklistCoreActionModal
          onClose={() => setShowSendToEkModal(false)}
          title="Send tilbake til EK"
          buttontext="Send til EK"
          nextStatus="EK"
        >
          <SKSendToEk />
        </ChecklistCoreActionModal>
      )}

      {showLegacyEkSkActions && showSendToSkModal && (
        <ChecklistCoreActionModal
          onClose={() => setShowSendToSkModal(false)}
          title="Send til SK"
          buttontext="Send til SK"
          nextStatus="SK"
        >
          <EkSendToSk />
        </ChecklistCoreActionModal>
      )}

      {showLegacyEkSkActions && showEkSendToGodkjenningModal && (
        <ChecklistCoreActionModal
          onClose={() => setShowEkSendToGodkjenningModal(false)}
          title="Send til godkjenning"
          buttontext="Send til godkjenning"
          nextStatus="APPROVED"
        >
          <EkSendForPublish />
        </ChecklistCoreActionModal>
      )}

      <Paper radius="md" p="sm" m="sm" bg="dark.8" withBorder>
        <Flex justify="space-between" align="center" gap="md" wrap="nowrap">
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Title order={4} c="white">
              Core dokument: <b>{docId}</b>
            </Title>
          </Stack>

          <Group align="center" gap="md" wrap="nowrap" style={{ marginLeft: "auto", flexShrink: 0 }}>
            {showLegacyEkSkActions && (
              <Group gap="xs" wrap="nowrap">
                {loggedInUserIsEK && (
                  <>
                    <Button
                      color="green"
                      onClick={() => setShowSendToSkModal(true)}
                      leftSection={<i className="fa fa-arrow-right fa-fw" />}
                    >
                      Send til SK
                    </Button>

                    <Button color="green" onClick={() => setShowEkSendToGodkjenningModal(true)}>
                      Klar til godkjenning
                    </Button>
                  </>
                )}

                {loggedInUserIsSK && (
                  <Button
                    color="green"
                    onClick={() => setShowSendToEkModal(true)}
                    leftSection={<i className="fa fa-arrow-left fa-fw" />}
                  >
                    Returner til EK
                  </Button>
                )}
              </Group>
            )}

            <RelatedChecklistsByReviewIdMenu />

            <DkApprovalPanel />
          </Group>
        </Flex>
      </Paper>

      {/* DK panel (viser alltid DK + status hvis DK finnes; knapp kun hvis innlogget er DK) */}
    </>
  );
};

export default DoksamChecklistBanner;

const SKSendToEk: React.FC = () => {
  const user = useAppSelector((state) => state.globalReducer.user);

  return (
    <Stack gap="xs" mt="md">
      <Text fw={600}>Bekreftelse – Send tilbake til EK</Text>

      <Text size="sm" lh={1.5}>
        Du er i ferd med å <b>sende sjekklisten tilbake til egenkontroll (EK)</b> for oppfølging og korrigering.
        Eventuelle merknader må være <b>registrert som kommentarer</b> i sjekklisten.
      </Text>

      <Text size="sm" lh={1.5}>
        <b>Dato:</b> {new Date().toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })}
        <br />
        <b>Avsender (SK):</b> {user?.name || ""}
      </Text>
    </Stack>
  );
};

const EkSendToSk: React.FC = () => {
  const user = useAppSelector((state) => state.globalReducer.user);

  return (
    <Stack gap="xs" mt="md">
      <Text fw={600}>Bekreftelse – Send til SK</Text>

      <Text size="sm" lh={1.5}>
        Du er i ferd med å <b>sende sjekklisten til sidemanskontroll (SK)</b> for oppfølging. Eventuelle merknader må
        være <b>registrert som kommentarer</b> i sjekklisten.
      </Text>

      <Text size="sm" lh={1.5}>
        <b>Dato:</b> {new Date().toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })}
        <br />
        <b>Avsender (SK):</b> {user?.name || ""}
      </Text>
    </Stack>
  );
};

const EkSendForPublish: React.FC = () => {
  const user = useAppSelector((state) => state.globalReducer.user);

  return (
    <Stack gap="xs" mt="md">
      <Text fw={600}>Kommentarbekreftelse - Utførte kontroller</Text>

      <Text size="sm" lh={1.5}>
        Det bekreftes at <b>alle relevante kommentarer</b> fra <b>alle utførte kontroller</b> er <b>innarbeidet</b> i
        sluttproduktet (signeres av egenkontrollør):
      </Text>

      <Text size="sm" lh={1.5}>
        <b>Dato:</b> {new Date().toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })}
        <br />
        <b>Signatur:</b> {user?.name || ""}
      </Text>

      <Text size="sm" lh={1.5}>
        Husk å signere og datere sjekk-kopi etter utført kontroll. Ved å signere sjekk-kopien verifiseres at
        dokument/tegning/modell/aktivitet er kontrollert i henhold til oppdragets prosedyrer.
      </Text>
    </Stack>
  );
};
